import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Search, Phone, Mail } from 'lucide-react';
import axios from 'axios';
import { siteConfig } from '@/config/siteConfig';
import { Helmet } from 'react-helmet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FAQ() {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQs();
    fetchCategories();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/faqs`);
      setFaqs(response.data);
      setFilteredFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/faq-categories`);
      setCategories(['All', ...response.data.categories]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    filterFAQs();
  }, [activeCategory, searchQuery, faqs]);

  const filterFAQs = () => {
    let filtered = faqs;

    // Filter by category
    if (activeCategory !== 'All') {
      filtered = filtered.filter((faq) => faq.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredFaqs(filtered);
  };

  // Group FAQs by category for display
  const groupedFaqs = filteredFaqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {});

  // Generate FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Real Estate FAQs - Buyers & Sellers Guide | {siteConfig.agentName}</title>
        <meta 
          name="description" 
          content="Get answers to the top 50 real estate questions for home buyers and sellers. Expert guidance from licensed realtor serving California and Florida." 
        />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Hero Section */}
        <section className="py-16 bg-white" data-testid="faq-hero">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1e3a8a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Top 50 Real Estate Questions for Buyers & Sellers
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search FAQs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 py-6 text-lg"
                  data-testid="faq-search-input"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Category Filters */}
        <section className="py-8 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  data-testid={`category-${category.toLowerCase()}`}
                  className={`px-6 py-2 rounded-full font-medium transition-colors $\{
                    activeCategory === category
                      ? 'bg-[#1e3a8a] text-white'
                      : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
            {searchQuery && (
              <p className="text-center mt-4 text-slate-600">
                Found {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </section>

        {/* FAQ Accordions */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-slate-600">Loading FAQs...</p>
              </div>
            ) : filteredFaqs.length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-slate-600">No FAQs found matching your search.</p>
              </Card>
            ) : (
              <div className="space-y-8">
                {activeCategory === 'All' ? (
                  // Show grouped by category
                  Object.keys(groupedFaqs).map((category) => (
                    <div key={category}>
                      <h2 className="text-2xl font-bold text-[#1e3a8a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {category === 'General' && '🏠 '}
                        {category === 'Buying' && '🔑 '}
                        {category === 'Selling' && '💰 '}
                        {category === 'Investment' && '📈 '}
                        {category === 'Legal' && '⚖️ '}
                        {category} Questions
                      </h2>
                      <Accordion type="single" collapsible className="space-y-2">
                        {groupedFaqs[category].map((faq) => (
                          <AccordionItem 
                            key={faq.id} 
                            value={faq.id}
                            className="bg-white rounded-lg border border-slate-200 px-6"
                            data-testid="faq-item"
                          >
                            <AccordionTrigger className="text-left hover:no-underline">
                              <span className="font-semibold text-slate-800">{faq.question}</span>
                            </AccordionTrigger>
                            <AccordionContent className="text-slate-600 pt-2 pb-4">
                              {faq.answer}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>

                      {/* CTA after every category */}
                      {Object.keys(groupedFaqs).indexOf(category) % 2 === 1 && (
                        <div className="my-8 p-6 bg-gradient-to-r from-[#1e3a8a] to-[#1e3a8a]/80 rounded-lg text-center text-white">
                          <p className="text-lg mb-4">Have more questions? I'm here to help!</p>
                          <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button
                              onClick={() => navigate('/contact')}
                              className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#1e3a8a]"
                              data-testid="cta-contact-btn"
                            >
                              <Phone className="mr-2 h-4 w-4" />
                              Contact Me
                            </Button>
                            <Button
                              onClick={() => navigate('/signup')}
                              variant="outline"
                              className="bg-white text-[#1e3a8a] hover:bg-slate-100"
                              data-testid="cta-signup-btn"
                            >
                              <Mail className="mr-2 h-4 w-4" />
                              Get Started
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  // Show single category
                  <div>
                    <Accordion type="single" collapsible className="space-y-2">
                      {filteredFaqs.map((faq) => (
                        <AccordionItem 
                          key={faq.id} 
                          value={faq.id}
                          className="bg-white rounded-lg border border-slate-200 px-6"
                          data-testid="faq-item"
                        >
                          <AccordionTrigger className="text-left hover:no-underline">
                            <span className="font-semibold text-slate-800">{faq.question}</span>
                          </AccordionTrigger>
                          <AccordionContent className="text-slate-600 pt-2 pb-4">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-[#1e3a8a] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Still Have Questions?
            </h2>
            <p className="text-xl mb-8">
              Contact {siteConfig.agentName} for personalized guidance and local insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#1e3a8a] px-8"
                data-testid="final-cta-contact"
              >
                Schedule a Consultation
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1e3a8a] px-8"
                data-testid="final-cta-signup"
              >
                Get Started Today
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
